// v1.20.1 · Gráficos simples en SVG para Resultados: líneas, barras apiladas y barras horizontales.
// Un solo eje por gráfico, leyenda con el total del periodo, guía y valores al pasar el mouse o el dedo.
import React, { useEffect, useRef, useState } from 'react';

export const C = { pagado: '#2a78d6', organico: '#eb6834', otros: '#1baf7a' };   // paleta categórica validada (fondo blanco)
const GRID = '#E5E7EB';
const INK2 = '#6B7280';
export const dshort = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', timeZone: 'America/Guayaquil' });

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const upd = () => setW(el.clientWidth);
    upd();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function niceMax(v, integer = false) {
  if (!(v > 0)) return integer ? 2 : 1;
  if (integer && v <= 10) return Math.ceil(v / 2) * 2;   // conteos: la línea del medio cae en un entero
  const p = 10 ** Math.floor(Math.log10(v));
  const m = v / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p;
}

export function Card({ title, sub, children, testId }) {
  return (
    <section className="min-w-0 space-y-2 rounded-xl border border-gray-200 bg-white p-3" data-testid={testId}>
      <div><h4 className="text-sm font-bold text-[#0F2A3A]">{title}</h4>{sub && <p className="text-xs text-gray-500">{sub}</p>}</div>
      {children}
    </section>
  );
}

function Legend({ series, fmt, swatch = 'line' }) {
  if (series.length < 2) return null;
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
      {series.map((s) => (
        <li key={s.name} className="flex items-center gap-1.5">
          <span aria-hidden className={swatch === 'line' ? 'h-0.5 w-4 rounded' : 'h-2.5 w-2.5 rounded-sm'} style={{ background: s.color }} />
          {s.name}{s.total != null && <b className="font-semibold tabular-nums text-[#0F2A3A]">{fmt(s.total)}</b>}
        </li>
      ))}
    </ul>
  );
}

function Tip({ x, w, day, rows }) {
  const left = Math.min(Math.max(x - 80, 0), Math.max(w - 160, 0));
  return (
    <div className="pointer-events-none absolute top-0 z-10 w-40 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs shadow-md" style={{ left }} data-testid="chart-tip">
      <p className="font-semibold text-[#0F2A3A]">{dshort(day)}</p>
      {rows.map(([name, color, v]) => (
        <p key={name} className="flex items-center justify-between gap-2 text-gray-700">
          <span className="flex items-center gap-1"><span aria-hidden className="h-2 w-2 rounded-full" style={{ background: color }} />{name}</span>
          <b className="tabular-nums text-[#0F2A3A]">{v}</b>
        </p>
      ))}
    </div>
  );
}

const H = 170, PADL = 44, PADR = 8, PADT = 8, PADB = 22;

function Frame({ w, max, fmt, labels, children, onMove, onLeave }) {
  const ih = H - PADT - PADB;
  const ticks = [0, 0.5, 1].map((t) => t * max);
  const xi = (i) => PADL + (labels.length < 2 ? (w - PADL - PADR) / 2 : (i * (w - PADL - PADR)) / (labels.length - 1));
  const show = labels.length <= 1 ? [0] : [0, Math.floor((labels.length - 1) / 2), labels.length - 1];
  return (
    <svg width={w} height={H} className="block touch-none select-none" onPointerMove={onMove} onPointerLeave={onLeave} onPointerDown={onMove}>
      {ticks.map((t) => {
        const y = PADT + ih - (t / max) * ih;
        return (
          <g key={t}>
            <line x1={PADL} x2={w - PADR} y1={y} y2={y} stroke={GRID} strokeWidth="1" />
            <text x={PADL - 6} y={y + 4} textAnchor="end" fontSize="10" fill={INK2}>{fmt(t)}</text>
          </g>
        );
      })}
      {show.map((i) => <text key={i} x={xi(i)} y={H - 6} textAnchor={i === 0 ? 'start' : i === labels.length - 1 ? 'end' : 'middle'} fontSize="10" fill={INK2}>{dshort(labels[i])}</text>)}
      {children({ xi, y: (v) => PADT + ih - (v / max) * ih, ih })}
    </svg>
  );
}

function useHover(w, n, slot) {
  const [i, setI] = useState(null);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left;
    const k = slot ? Math.floor((x - PADL) / slot) : Math.round(((x - PADL) / Math.max(w - PADL - PADR, 1)) * (n - 1));
    setI(k >= 0 && k < n ? k : null);
  };
  return [i, onMove, () => setI(null)];
}

/** Líneas en un mismo eje. series: [{name, color, values (null = sin dato), total}] */
export function LineChart({ labels, series, fmt, testId }) {
  const [ref, w] = useWidth();
  const [hi, onMove, onLeave] = useHover(w, labels.length);
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values.filter((v) => v != null))));
  const empty = !series.some((s) => s.values.some((v) => v));
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Legend series={series} fmt={fmt} />
      <div ref={ref} className="relative">
        {empty ? <p className="flex h-[120px] items-center justify-center text-xs text-gray-400">Aún sin datos en el periodo</p> : w > 0 && (
          <Frame w={w} max={max} fmt={fmt} labels={labels} onMove={onMove} onLeave={onLeave}>
            {({ xi, y }) => (
              <>
                {hi != null && <line x1={xi(hi)} x2={xi(hi)} y1={PADT} y2={H - PADB} stroke="#9CA3AF" strokeDasharray="3 3" />}
                {series.map((s) => {
                  const segs = [];
                  let cur = [];
                  s.values.forEach((v, i) => { if (v == null) { if (cur.length) segs.push(cur); cur = []; } else cur.push(`${xi(i)},${y(v)}`); });
                  if (cur.length) segs.push(cur);
                  return (
                    <g key={s.name}>
                      {segs.map((p) => (p.length > 1
                        ? <polyline key={p[0]} points={p.join(' ')} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                        : <circle key={p[0]} cx={p[0].split(',')[0]} cy={p[0].split(',')[1]} r="3" fill={s.color} />))}
                      {hi != null && s.values[hi] != null && <circle cx={xi(hi)} cy={y(s.values[hi])} r="4.5" fill={s.color} stroke="#fff" strokeWidth="2" />}
                    </g>
                  );
                })}
              </>
            )}
          </Frame>
        )}
        {hi != null && !empty && <Tip x={labels.length < 2 ? w / 2 : PADL + (hi * (w - PADL - PADR)) / (labels.length - 1)} w={w} day={labels[hi]} rows={series.map((s) => [s.name, s.color, s.values[hi] == null ? '—' : fmt(s.values[hi])])} />}
      </div>
    </div>
  );
}

/** Barras por día, apiladas si hay varias series (partes de un total). */
export function BarChart({ labels, series, fmt, testId, integer = false }) {
  const [ref, w] = useWidth();
  const n = labels.length;
  const slot = n ? (w - PADL - PADR) / n : 0;
  const [hi, onMove, onLeave] = useHover(w, n, slot);
  const totals = labels.map((_, i) => series.reduce((a, s) => a + (s.values[i] || 0), 0));
  const max = niceMax(Math.max(0, ...totals), integer);
  const bw = Math.max(2, Math.min(28, slot - 2));
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Legend series={series} fmt={fmt} swatch="box" />
      <div ref={ref} className="relative">
        {!totals.some(Boolean) ? <p className="flex h-[120px] items-center justify-center text-xs text-gray-400">Aún sin datos en el periodo</p> : w > 0 && (
          <Frame w={w} max={max} fmt={fmt} labels={labels} onMove={onMove} onLeave={onLeave}>
            {({ y }) => labels.map((d, i) => {
              const cx = PADL + slot * i + slot / 2;
              let base = 0;
              const parts = series.map((s) => ({ s, v: s.values[i] || 0 })).filter((p) => p.v > 0);
              return (
                <g key={d} opacity={hi == null || hi === i ? 1 : 0.45}>
                  {parts.map(({ s, v }, k) => {
                    const y0 = y(base), y1 = y(base + v);
                    base += v;
                    const top = k === parts.length - 1;
                    const h = Math.max(1, y0 - y1 - (top ? 0 : 1));   // 1 px de separación entre segmentos
                    const x = cx - bw / 2, r = top ? Math.min(4, bw / 2, h) : 0;
                    return <path key={s.name} fill={s.color}
                                 d={`M${x},${y1 + h} V${y1 + r} Q${x},${y1} ${x + r},${y1} H${x + bw - r} Q${x + bw},${y1} ${x + bw},${y1 + r} V${y1 + h} Z`} />;
                  })}
                </g>
              );
            })}
          </Frame>
        )}
        {hi != null && totals.some(Boolean) && (
          <Tip x={PADL + slot * hi + slot / 2} w={w} day={labels[hi]}
               rows={[...series.map((s) => [s.name, s.color, fmt(s.values[hi] || 0)]), ...(series.length > 1 ? [['Total', '#0F2A3A', fmt(totals[hi])]] : [])]} />
        )}
      </div>
    </div>
  );
}

/** Barras horizontales (embudo, nichos): una fila por etapa, valor y nota a la derecha. */
export function HBars({ rows, color = C.pagado, fmt, testId }) {
  const max = Math.max(1, ...rows.map((r) => r.value || 0));
  return (
    <ul className="space-y-2" data-testid={testId}>
      {rows.map((r) => (
        <li key={r.label} className="space-y-0.5">
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="text-gray-700">{r.label}</span>
            <span className="tabular-nums"><b className="text-[#0F2A3A]">{fmt(r.value)}</b>{r.note && <span className="ml-1.5 text-gray-500">{r.note}</span>}</span>
          </div>
          <div className="h-2.5 rounded-full bg-gray-100" title={`${r.label}: ${fmt(r.value)}${r.note ? ` · ${r.note}` : ''}`}>
            <div className="h-2.5 rounded-full" style={{ width: `${r.value ? Math.max(2, (r.value / max) * 100) : 0}%`, background: color }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
